import type { I18nKey } from './keys'

export const en: Partial<Record<I18nKey, string>> = {
  'app.loading': 'Loading…',
  'app.loading_modal': 'Loading modal…',
  'app.loading_editor': 'Loading editor…',
  'app.loading_ai_config': 'Loading AI settings…',

  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.create': 'Create',

  'lang.switcher_label': 'App language',
  'lang.es': 'ES',
  'lang.en': 'EN',

  'projects.beta_checking': 'Checking access…',
  'projects.beta_closed': 'Limited access',
  'projects.sign_in': 'Sign in',
  'projects.title': 'Writer\'s Lens',
  'projects.subtitle': 'Your Writing Projects',
  'projects.new_project': 'New Project',
  'projects.import_project': 'Import Project',
  'projects.install_example': 'Install guided project (ES/EN)',
  'projects.install_example_running': 'Installing guide…',
  'projects.install_example_success': 'Guided projects installed: {created}. Already present: {skipped}.',
  'projects.install_example_already': 'Guided projects were already installed.',
  'projects.install_example_error': 'Could not install guided project.',
  'projects.resume_last_session': 'Last session',
  'projects.resume_syncing': 'Finding your place…',
  'projects.resume_last_used': 'Last used {time}',
  'projects.resume_last_synced_from_device': 'Last synced from {device}',
  'projects.resume_device_mobile': 'mobile',
  'projects.resume_device_desktop': 'desktop',
  'projects.resume_workspace_editor': 'Continue writing',
  'projects.resume_workspace_global_lens': 'Continue in Atlas',
  'projects.resume_workspace_idea_lens': 'Continue in Idea Lens',
  'projects.resume_restore_exact': 'Restored your last working spot.',
  'projects.resume_restore_approximate': 'We reopened the right place, but the exact fragment moved.',
  'projects.resume_restore_incomplete': 'We restored the context, but the exact re-entry did not fully settle.',
  'projects.resume_restore_stale': 'Your last saved spot is no longer available. We reopened the closest safe place.',
  'projects.resume_restore_workspace_global_lens': 'Restored your last Atlas workspace.',
  'projects.resume_restore_workspace_idea_lens': 'Restored your last Idea Lens workspace.',
  'projects.footer_privacy': 'Privacy',
  'projects.footer_terms': 'Terms',
  'projects.footer_security': 'Security',
  'projects.footer_closed_beta': 'Public beta · Writer\'s Lens',

  'project_card.status.draft': 'Draft',
  'project_card.status.in_progress': 'In progress',
  'project_card.status.review': 'Review',
  'project_card.status.completed': 'Completed',
  'project_card.by_author': 'by {author}',
  'project_card.progress': 'Progress',
  'project_card.words_progress': '{progress} / {target} words',
  'project_card.completed_pct': '{pct}% completed',

  'header.no_project': 'No project',
  'header.app_subtitle': 'Your creative writing assistant',
  'header.sign_in': 'Sign in',
  'header.beta_closed': 'Limited access',
  'header.protection': 'Protection',
  'header.protection_no_key': 'No key',
  'header.protection_ready': 'Protected',
  'header.protection_title': 'Protection status (click to manage key)',
  'header.search_project': 'Search project',
  'header.open_project_tools': 'Open project tools',

  'usermenu.premium_account': 'Premium account',
  'usermenu.plan_free': 'Free plan',
  'usermenu.plan_premium': 'Premium plan',
  'usermenu.debug': 'Debug',
  'usermenu.sync_cloud': 'Cloud sync',
  'usermenu.sync_diagnostics': 'Sync diagnostics',
  'usermenu.sync_blocked_beta': 'Progressive access: cloud sync is not available for this account yet.',
  'usermenu.sync_blocked_plan': 'Premium: cloud sync is available on the Premium plan according to the current profile.',
  'usermenu.sync_plan_hint': 'Check `profiles.plan` if this account should already be Premium.',
  'usermenu.sync_availability': 'Availability',
  'usermenu.sync_state_active': 'Active',
  'usermenu.sync_state_inactive': 'Inactive',
  'usermenu.sync_state_blocked': 'Blocked',
  'usermenu.sync_state_review_required': 'Needs review',
  'usermenu.sync_state_remote_recovery': 'Remote recovery completed',
  'usermenu.sync_state_warnings': 'Sync with warnings',
  'usermenu.sync_syncing': 'Syncing…',
  'usermenu.sync_starting': 'Starting…',
  'usermenu.sync_analyzing': 'Analyzing…',
  'usermenu.sync_last': 'Last sync: {time}',
  'usermenu.sync_never': 'Never synced in this session',
  'usermenu.sync_no_project': 'Open a project to sync.',
  'usermenu.sync_offline': 'Offline',
  'usermenu.sync_fast_ok': 'Sync completed without risks (Safe Sync was not required).',
  'usermenu.sync_warn_metadata_phase': 'Metadata phases timed out (projects/versions); content push continued.',
  'usermenu.sync_warn_assets_pending': 'The image could not be uploaded to Supabase; the text was synchronized.',
  'usermenu.sync_done_with_warnings': 'Sync completed with warnings.',
  'usermenu.sync_auth_fallback': 'Session running in Safari compatibility mode (local fallback).',
  'usermenu.vault_active': 'Encrypted vault active',
  'usermenu.sync_now_hint': 'Active project sync now: tries fast sync and opens Safe sync when risks are detected.',
  'usermenu.sync_safe_hint': 'Active project safe sync: analyzes conflicts and lets you choose before applying changes.',
  'usermenu.sync_now_tooltip': 'Fast sync. If risks or backend degradation are detected, Safe sync opens automatically.',
  'usermenu.sync_safe_tooltip': 'Open conflict analysis to decide manually what to keep.',
  'usermenu.sync_now': 'Sync now',
  'usermenu.sync_safe': 'Safe sync…',
  'usermenu.close_project': 'Close project',
  'usermenu.switch_account_hint': 'To test another account in this browser, sign out and sign in again. For simultaneous sessions use another profile or incognito.',
  'usermenu.sign_out': 'Sign out',
  'outline.part_badge': 'Part',
  'outline.add_part': 'Add part',

  'login.title_signup': 'Create Account',
  'login.title_signin': 'Sign In',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit_signup': 'Create Account',
  'login.submit_signin': 'Sign In',
  'login.loading': 'Loading...',
  'login.close': 'Close',
  'login.unexpected_error': 'Unexpected error. Please try again.',
  'login.github_error': 'GitHub error: {message}',
  'login.github_unexpected': 'Unexpected error connecting with GitHub.',
  'login.or': 'OR',
  'login.github_continue': 'Continue with GitHub',
  'login.github_connecting': 'Connecting...',
  'login.toggle_to_signin': 'Already have an account? Sign in',
  'login.toggle_to_signup': 'Don\'t have an account? Sign up',
  'login.msg_invalid_credentials': 'Invalid email or password',
  'login.msg_confirm_email': 'Please confirm your email before signing in',
  'login.msg_email_registered': 'This email is already registered. Try signing in.',
  'login.msg_redirect': 'Check Redirect URLs in Supabase Auth (include your Netlify domain and localhost).',
  'login.msg_password_short': 'Password must be at least 6 characters',
  'login.msg_signup_success': 'Sign-up successful. Check your email confirmation link, then sign in.',

  'import.title': 'Import Project',
  'import.select_file': 'Select an exported file (.txt, .md or .json).',
  'import.file': 'File',
  'import.detected_title': 'Detected title',
  'import.author': 'Author',
  'import.author_default': 'Author',
  'import.language': 'Language',
  'import.sections_detected': 'Detected sections',
  'import.sync_blocked_notice': 'This project will be imported with remote sync blocked until you set a local key in Gray Matter Zone.',
  'import.cancel': 'Cancel',
  'import.import_project': 'Import project',
  'import.importing': 'Importing...',
  'import.parse_error': 'Could not parse file.',
  'import.import_error': 'Could not import project.',

  'export.title': 'Export document',
  'export.format': 'Format',
  'export.format_docx': 'DOCX',
  'export.format_pdf_soon': 'PDF (coming soon)',
  'export.status': 'Status',
  'export.status_draft': 'Draft',
  'export.status_confidential': 'Confidential',
  'export.status_final': 'Final',
  'export.include_metadata': 'Include metadata (author/version/date/id)',
  'export.watermark': 'Watermark',
  'export.pagination': 'Pagination',
  'export.export': 'Export',
  'export.exporting': 'Exporting…',

  'psm.header_edit_title': 'Edit Project',
  'psm.header_create_title': 'Create New Project',
  'psm.header_edit_subtitle': 'Update metadata, key and maintenance',
  'psm.header_create_subtitle': 'Set initial details',
  'psm.header_targets_subtitle': 'Adjust the target and word range',
  'psm.header_targets_hint': 'Open this section to recalibrate manuscript scope',
  'psm.header_references_subtitle': 'Project bibliography summary',
  'psm.header_references_hint': 'Check the status here and open the full library when you need it',
  'psm.alert_select_genre': 'Select a genre',
  'psm.alert_invalid_genre': 'Invalid genre',
  'psm.saved': 'Changes saved',
  'psm.basic_info': 'Basic information',
  'psm.genre': 'Genre',
  'psm.word_targets': 'Word targets',
  'editor.pending_badge_suffix': 'pending',
  'editor.pending_badge_title': 'Show pending sections',
  'editor.pending_badge_aria': '{count} pending. Show detail',
  'editor.pending_tooltip_heading': 'Still pending',
  'editor.pending_tooltip_more': 'more',
  'editor.pending_status_todo': 'Pending',
  'editor.pending_status_draft': 'Draft',
  'editor.continue_writing': 'Continue writing',
  'editor.open_and_write': 'Open and write',
  'editor.next_pending': 'Next',
  'editor.progress_pill_label': 'Pace',
  'editor.target_pill_label': 'Target',
  'editor.edit_target_words': 'Edit word target',
  'workspace.insert_figure': 'Insert figure',
  'workspace.insert_figure_short': 'Figure',
  'workspace.insert_figure_title': 'Insert figure into the manuscript',
  'workspace.insert_citation': 'Insert citation',
  'workspace.insert_references': 'Insert references',
  'workspace.insert_references_short': 'References',
  'workspace.insert_references_title': 'Insert references into the manuscript',
  'workspace.manage_bibliography': 'Bibliography',
  'workspace.manage_bibliography_empty': 'Project bibliography',
  'workspace.manage_bibliography_with_count': 'Project bibliography, {{count}} references',
  'workspace.manage_bibliography_with_issues': 'Project bibliography, {{count}} incomplete references',
  'workspace.manage_figures': 'Manage figures',
  'workspace.manage_figures_short': 'Figures',
  'workspace.manage_figures_title': 'Manage section figures',
  'workspace.manage_bibliography_title': 'Open project bibliography',
  'workspace.select_section_first': 'Select a section to use this action',
  'workspace.reference_targets_figures': 'Figures',
  'workspace.reference_targets_callouts': 'Callouts',
  'workspace.reference_targets_chapters': 'Other chapters',
  'workspace.reference_bibliography_picker_hint': 'Find the reference first, then add a locator if needed.',
  'workspace.reference_bibliography_locator_label': 'Locator',
  'workspace.reference_bibliography_locator_placeholder': 'p. 42',
  'workspace.reference_bibliography_no_results_title': 'No references match this search',
  'workspace.reference_bibliography_no_results_body': 'Clear the search or open the library to create or fix references.',
  'workspace.reference_bibliography_clear_search': 'Clear search',
  'workspace.coming_soon': 'Coming soon',
  'workspace.project_tools_subtitle': 'Secondary actions and project tools.',
  'editor.typewriter_active': 'Typewriter active',
  'editor.typewriter_anchor_aria': 'Move the typewriter writing line',
  'editor.typewriter_anchor_hint': 'Adjust the writing line height',
  'psm.encryption_zone': 'Encryption zone',
  'psm.references_heading': 'Bibliography',
  'psm.references_help': 'Manage the project citation library here. You can then insert `[@citeKey]` tokens directly into the manuscript.',
  'psm.references_summary_help': 'Check the project bibliography status here and open the editorial library to create, review, and complete references.',
  'psm.references_open_library': 'Open library',
  'psm.references_modal_subtitle': 'Editorial library to prepare and review the project citations.',
  'psm.references_modal_total': 'references',
  'psm.references_modal_ready': 'ready to cite',
  'psm.references_modal_list': 'Library',
  'psm.references_modal_editor': 'Active reference',
  'psm.references_modal_empty': 'There are no references in this library yet.',
  'psm.references_modal_missing_key': 'Missing cite key',
  'psm.references_explorer_hint': 'Search, filter, and jump straight to the reference you need.',
  'psm.references_search_placeholder': 'Search by title, cite key, or author',
  'psm.references_filter_all': 'All',
  'psm.references_filter_book': 'Books',
  'psm.references_filter_journal_article': 'Articles',
  'psm.references_filter_webpage': 'Web',
  'psm.references_filter_other': 'Other',
  'psm.references_filter_incomplete': 'Incomplete',
  'psm.references_empty_title': 'Your library is still empty',
  'psm.references_empty_body': 'Start by creating a reference, then refine it in the same editorial flow you use when citing it.',
  'psm.references_empty_action': 'Add reference',
  'psm.references_no_results_title': 'No results for this view',
  'psm.references_no_results_body': 'Try a different search or clear the filters to see the full library again.',
  'psm.references_no_results_clear': 'Clear filters',
  'psm.references_editor_hint': 'Edit the selected reference and save when it is ready to cite.',
  'psm.references_editor_empty': 'Add a reference to start building the project bibliography.',
  'psm.references_editor_empty_filtered': 'No reference matches the current filters. Clear the search or change the classification.',
  'psm.references_return_to_picker': 'Back to picker',
  'psm.references_modal_add_author': 'Add author',
  'psm.references_modal_remove_author': 'Remove author',
  'psm.references_add': 'Add reference',
  'psm.references_new': 'New reference',
  'psm.references_delete': 'Delete',
  'psm.references_cite_key': 'Cite key',
  'psm.references_type': 'Type',
  'psm.references_title': 'Title',
  'psm.references_year': 'Year',
  'psm.references_publisher': 'Publisher / institution',
  'psm.references_journal': 'Journal / publication',
  'psm.references_pages': 'Pages',
  'psm.references_url': 'URL',
  'psm.references_authors': 'Authors',
  'psm.references_authors_hint': 'Keep the signing order exactly as you want it cited.',
  'psm.references_author_given': 'Given name',
  'psm.references_author_family': 'Family name',
  'psm.references_type_book': 'Book',
  'psm.references_type_journal_article': 'Article',
  'psm.references_type_webpage': 'Web',
  'psm.references_type_report': 'Report',
  'psm.references_type_chapter': 'Chapter',
  'psm.references_type_thesis': 'Thesis',
  'psm.references_type_other': 'Other',
  'psm.footer_label': 'Form actions',
  'psm.footer_unsaved': 'Unsaved changes',
  'psm.footer_clean': 'No changes',
  'psm.footer_create_hint': 'Configure and create your new project',
  'psm.footer_pending': 'Pending save',
  'psm.footer_shortcuts': 'Shortcuts: {saveShortcut} save, {closeShortcut} close',
  'psm.footer_cancel': 'Cancel',
  'psm.footer_primary_create': 'Create',
  'psm.footer_primary_close': 'Close',
  'psm.footer_primary_save': 'Save',

  'sync.title': 'Safe sync',
  'sync.subtitle': 'Choose which version to keep for each changed section.',
  'sync.close_force': 'Force close',
  'sync.conflicts_apply_all': '{count} conflict{suffix}. Apply to all:',
  'sync.apply_local': 'Use all local',
  'sync.apply_remote': 'Use all remote',
  'sync.error_enable_sync': 'Enable "Cloud sync" to run Safe Sync.',
  'sync.error_beta_blocked': 'Progressive access: cloud sync is not available for this account yet.',
  'sync.error_timeout': 'Sync took too long. You can close and retry.',
  'sync.retry': 'Retry safe sync',
  'sync.degraded_timeout': 'Degraded sync mode: remote counting timed out. You can continue with manual review, retry, or push local-only changes.',
  'sync.degraded_error': 'Degraded sync mode: {error}',
  'sync.degraded_generic': 'the remote backend did not respond stably.',
  'sync.degraded_unverified_title': 'Remote state could not be verified',
  'sync.degraded_unverified_desc': 'Pending local changes in this project: {versions} versions, {sections} sections, {contents} contents.',
  'sync.degraded_push_confirm': 'I understand the risk and want to push only my local changes for the active project.',
  'sync.degraded_push_confirm_required': 'Confirm local-only push before continuing.',
  'sync.degraded_push_no_changes': 'There are no pending local changes to push in degraded mode.',
  'sync.degraded_push_action': 'Push local-only changes',
  'sync.strict_blocked_unverified': 'Strict flow blocked: remote state is not verifiable. Use "Push local-only changes" or retry analysis.',
  'sync.retry_analysis': 'Retry analysis',
  'sync.preparing': 'Preparing analysis...',
  'sync.running': 'Syncing… (it can take up to 90 seconds)',
  'sync.no_conflicts': 'No conflicts found. Press "Run safe sync" to continue.',
  'sync.no_conflicts_degraded': 'No verifiable conflicts. Strict mode remains blocked while remote state is unverified.',
  'sync.resolution': 'Resolution',
  'sync.option_keep_local': 'Keep local (device)',
  'sync.option_keep_remote': 'Keep remote (server)',
  'sync.option_preserve_both': 'Preserve both (creates conflict version)',
  'sync.option_abort': 'Abort sync',
  'sync.warning_overwrite_local': 'Warning: local changes will be overwritten with server content.',
  'sync.no_decision_needed': 'No decision required',
  'sync.run': 'Run safe sync',
  'sync.choose_resolution': 'Choose a resolution for each conflict.',
  'sync.counts_line': 'Local: {lp} proj · {lv} ver · {ls} sec · {lc} txt | Remote: {rp} · {rv} · {rs} · {rc}',

  'editor.ai_assistant': 'Legacy AI Assistant',
  'editor.close_ai_assistant': 'Close legacy AI assistant',
  'editor.zen_structure_badge_title': 'View project outline',
  'editor.zen_structure_badge_aria': 'Active section: {label}. Tap to open the project outline.',
  'editor.zen_structure_hint_title': 'Project outline lives here',
  'editor.zen_structure_hint_body': 'Tap to open the structure.',
  'lens.action': 'Ideas',
  'lens.action_open': 'Open Global Lens',
  'global_lens.shell_badge': 'Global Lens',
  'global_lens.shell_title': 'Project editorial atlas',
  'global_lens.back_to_editor': 'Back to manuscript',
  'global_lens.mobile_tab.structure': 'Structure',
  'global_lens.mobile_tab.tree': 'Tree',
  'global_lens.mobile_tab.actions': 'Actions',
  'global_lens.no_project': 'Open a project to inspect its global outline.',
  'global_lens.empty_title': 'There are no chapters in this project yet.',
  'global_lens.empty_body': 'Create the structure first, then use Global Lens to review the full thread.',
  'global_lens.health_badge': 'Global diagnosis',
  'global_lens.health_title': 'Book health map',
  'global_lens.health_progress': 'Editorial progress',
  'global_lens.health_total_sections': '{count} sections',
  'global_lens.health_requires_attention': '{count} need attention',
  'global_lens.health_no_ideas': '{count} with no ideas',
  'global_lens.health_marked_for_delete': '{count} marked for pruning',
  'global_lens.health_grouped_or_materialized': '{count} grouped or materialized',
  'global_lens.recommended_label': 'Recommended move',
  'global_lens.focus_next': 'Next focus',
  'global_lens.focus_target': 'Focus {count}',
  'global_lens.structure_badge': 'Structure',
  'global_lens.structure_title': 'Chapters and subsections',
  'global_lens.structure_attention': 'Attention',
  'global_lens.header_badge': 'Focused section',
  'global_lens.linked_ideas': '{count} linked ideas',
  'global_lens.loose_ideas': '{count} loose ideas',
  'global_lens.board_groups': '{count} grouped ideas',
  'global_lens.open_board': 'Work on board',
  'global_lens.open_manuscript': 'Go to manuscript',
  'global_lens.view_container': 'View container',
  'global_lens.mark_delete': 'Mark for deletion',
  'global_lens.unmark_delete': 'Remove mark',
  'global_lens.next_section': 'Next section',
  'global_lens.column_roots': 'Parts',
  'global_lens.column_roots_eyebrow': 'Opened book',
  'global_lens.column_children': 'Subsections',
  'global_lens.column_ideas': 'Ideas',
  'global_lens.column_empty': 'Empty',
  'global_lens.column_empty_eyebrow': 'Terminal section',
  'global_lens.column_sections': 'Sections',
  'global_lens.column_subsections': 'Subsections',
  'global_lens.column_children_count': '{count} children',
  'global_lens.column_direct_ideas_hint': '{count} direct ideas waiting',
  'global_lens.column_direct_ideas_count': '{count} direct ideas',
  'global_lens.metric.no_ideas': 'No ideas',
  'global_lens.metric.loose_ideas': 'Loose ideas',
  'global_lens.metric.prune': 'Prune',
  'global_lens.metric.direct_loose_ideas_label': 'Direct loose ideas',
  'global_lens.metric.direct_loose_ideas': '{count} direct loose ideas',
  'global_lens.metric.context_ideas': 'Context ideas',
  'global_lens.metric.words': 'words',
  'global_lens.metric.child_sections': 'Child sections',
  'global_lens.metric.hidden_direct_ideas_label': 'Hidden direct ideas',
  'global_lens.metric.hidden_direct_ideas': '{count} hidden direct ideas',
  'global_lens.metric.direct_ideas': 'Direct ideas',
  'global_lens.metric.connected_ideas': 'Connected ideas',
  'global_lens.empty_editorial_title': 'This section does not have its own thread yet',
  'global_lens.mobile_breadcrumbs': 'Visible columns',
  'global_lens.mobile_prev_column': 'Previous',
  'global_lens.mobile_next_column': 'Next',
  'global_lens.tree_badge': 'Idea tree',
  'global_lens.tree_title': 'Section idea structure',
  'global_lens.no_connections': 'This section has ideas, but they do not yet form a connected tree. Review them on the board and decide the thread.',
  'global_lens.no_ideas_title': 'There are no ideas in this section yet',
  'global_lens.no_ideas_body': 'This section does not have its own ideas yet. Open the board and start building its thread.',
  'global_lens.no_ideas_with_context': 'This section does not have its own ideas yet. Its container does have material, so you can jump back there or start this section on the board.',
  'global_lens.loose_badge': 'Loose ideas',
  'global_lens.loose_title': 'Ideas without a clear connection',
  'global_lens.loose_empty': 'There are no loose ideas in this section.',
  'global_lens.loose_empty_no_ideas': 'Loose ideas for this section will appear here once you start planning it.',
  'global_lens.open_in_board': 'Open on board',
  'global_lens.toggle_subsections': 'Sub-sections',
  'global_lens.toggle_direct_ideas': 'Direct ideas',
  'global_lens.secondary_relations': '+{count} relations',
  'global_lens.idea_inserted': 'Inserted',
  'global_lens.idea_pending': 'Pending',
  'global_lens.idea_notes_empty': 'No notes yet.',
  'global_lens.scope_only': 'Scope: this section only',
  'global_lens.scope_with_descendants': 'Scope: this section and descendants',
  'global_lens.context_label': 'Context: {label}',
  'global_lens.mobile_focus': 'Tree for: {label}',
  'global_lens.structure_context': 'Context',
  'global_lens.structure_current': 'Current',
  'global_lens.structure_delete_badge': 'Delete',
  'global_lens.chapter_status.no_ideas': 'No ideas',
  'global_lens.chapter_status.loose_ideas': 'Loose ideas',
  'global_lens.chapter_status.grouped_in_board': 'Grouped on board',
  'global_lens.chapter_status.materialized': 'Materialized',
  'global_lens.chapter_status.marked_for_delete': 'Marked for deletion',
  'global_lens.action.start_ideation': 'Start ideation',
  'global_lens.action.connect_loose_ideas': 'Connect loose ideas',
  'global_lens.action.prune_candidate': 'Prune candidate',
  'global_lens.action.expand_section': 'Expand section',
  'global_lens.action.continue_draft': 'Continue draft',
  'global_lens.reason.no_ideas': 'This section has no idea thread yet. Start the board before writing blind.',
  'global_lens.reason.loose_ideas': 'This section has loose ideas without a resolved thread. Connect them before expanding the draft.',
  'global_lens.reason.delete_candidate': 'This section is already marked for pruning. Decide whether it stays or goes.',
  'global_lens.reason.direct_ideas_hidden': 'This section already has direct ideas, but its child structure is hiding the leverage. Open it and decide the thread.',
  'global_lens.reason.materialized': 'This section already has materialized draft work. Continue the draft instead of branching wider.',
  'global_lens.reason.grouped_in_board': 'This section has grouped material on the board. Expand it into sharper draft progress.',
  'lens.panel_tab.analysis': 'Analysis',
  'lens.panel_tab.ideas': 'Ideas',
  'lens.close': 'Close Lenses',
  'lens.panel_title': 'Lens Panel',
  'lens.panel_subtitle': 'Local editorial analysis over the active manuscript.',
  'idea_lens.no_project': 'Open a project to activate Idea Lens.',
  'idea_lens.panel_subtitle': 'Project idea map.',
  'idea_lens.surface_badge': 'Idea Lens',
  'idea_lens.title': 'Visual draft board',
  'idea_lens.subtitle': 'Capture ideas and place them where they help the essay.',
  'idea_lens.workspace_title': 'Visual draft board',
  'idea_lens.workspace_title_vnext': 'Essay idea board',
  'idea_lens.workspace_hint': 'Capture an idea, place it in a chapter, and recover it while drafting.',
  'idea_lens.workspace_focus_hint': 'Idea Lens is already open in the main workspace.',
  'idea_lens.workspace_empty_hint': 'Open the wide map to create and arrange ideas.',
  'idea_lens.mode_board': 'Board',
  'idea_lens.mode_manuscript': 'Manuscript',
  'idea_lens.board_hint': 'Move ideas, connect them, and only then bring them into the manuscript.',
  'idea_lens.manuscript_hint': 'Assign each idea to a section and define where else it helps.',
  'idea_lens.new_placeholder': 'Capture a short idea',
  'idea_lens.new_placeholder_board': 'Capture a short idea',
  'idea_lens.create': 'Create',
  'idea_lens.autolink_hint': 'It starts in {section}. You can move it or add support sections later.',
  'idea_lens.spawn_selected_hint': 'The new idea will appear near the selected one.',
  'idea_lens.spawn_center_hint': 'The new idea will appear in the center of the board.',
  'idea_lens.empty': 'There are no ideas yet.',
  'idea_lens.empty_short': 'Create the first one above and start shaping the argument.',
  'idea_lens.board_empty_title': 'There are no ideas on the board yet.',
  'idea_lens.board_empty_hint': 'Create the first one above and start shaping the thread of the essay.',
  'idea_lens.cluster_label': 'Chapter',
  'idea_lens.go_to_section': 'Open chapter',
  'idea_lens.empty_notes': 'Add a short note.',
  'idea_lens.detail_title': 'Active idea',
  'idea_lens.board_inspector': 'Active idea',
  'idea_lens.manuscript_inspector': 'Manuscript placement',
  'idea_lens.delete': 'Delete',
  'idea_lens.field_title': 'Title',
  'idea_lens.field_type': 'Type',
  'idea_lens.field_notes': 'Notes',
  'idea_lens.field_notes_hint': 'Write naturally',
  'idea_lens.notes_placeholder': 'Capture the angle, example, or transition you want to remember.',
  'idea_lens.section_links': 'Place in the manuscript',
  'idea_lens.link_current': 'Use here',
  'idea_lens.unlink_current': 'Remove link',
  'idea_lens.no_links': 'This idea is not linked to any section yet.',
  'idea_lens.open_picker': 'Choose section',
  'idea_lens.close_picker': 'Close picker',
  'idea_lens.link_picker_hint': 'Choose one main section and, if needed, mark other sections or subsections where this idea also helps.',
  'idea_lens.link_picker_title': 'Manuscript tree',
  'idea_lens.link_picker_subtitle': 'Mark extra support',
  'idea_lens.tree_root': 'Chapter',
  'idea_lens.tree_child': 'Subsection',
  'idea_lens.tree_active': 'In focus',
  'idea_lens.linked_state': 'Linked',
  'idea_lens.unlinked_state': 'Free',
  'idea_lens.linked_summary': '{count} linked sections.',
  'idea_lens.primary_chapter': 'Main chapter',
  'idea_lens.primary_section': 'Main section',
  'idea_lens.extra_sections': 'Also helps in',
  'idea_lens.choose_chapter_first': 'Choose the main chapter first.',
  'idea_lens.choose_section_first': 'Choose the main section first.',
  'idea_lens.no_extra_sections': 'This chapter has no subsections to mark.',
  'idea_lens.no_extra_targets': 'There are no other sections left to mark.',
  'idea_lens.move_to_current_chapter': 'Move to current chapter',
  'idea_lens.move_to_current_section': 'Move to current section',
  'idea_lens.use_here': 'Use here',
  'idea_lens.assign_to_manuscript': 'Assign to manuscript',
  'idea_lens.board_assignment_empty': 'It does not belong to any section yet.',
  'idea_lens.placement_with_extras': '{chapter} + {count} supports',
  'idea_lens.group_count': '{count} ideas',
  'idea_lens.extra_count': '+{count} extras',
  'idea_lens.selection_title': 'Selection',
  'idea_lens.selection_count': '{count} selected ideas',
  'idea_lens.selection_empty': 'Select one or more ideas to group them.',
  'idea_lens.selection_none': 'No selection',
  'idea_lens.selection_single': '1 selected',
  'idea_lens.selection_multiple': '{count} selected',
  'idea_lens.draft_chapter_title': 'Draft chapters',
  'idea_lens.board_group_title': 'Board group',
  'idea_lens.board_group_no_group': 'No group',
  'idea_lens.board_group_materialized': 'Converted to section',
  'idea_lens.manuscript_chapter_title': 'Section in manuscript',
  'idea_lens.draft_chapter_badge': 'Draft chapter',
  'idea_lens.board_group_category': 'Chapter',
  'idea_lens.board_group_status_draft': 'Draft',
  'idea_lens.board_group_status_in_manuscript': 'In manuscript',
  'idea_lens.board_group_status_primary_here': 'Active location',
  'idea_lens.board_group_status_secondary_here': 'Supports here',
  'idea_lens.draft_chapter_inspector': 'Draft chapter inspector',
  'idea_lens.draft_chapter_title_label': 'Chapter title',
  'idea_lens.draft_chapter_sequence_label': 'Sequence label',
  'idea_lens.draft_chapter_status': 'Chapter status',
  'idea_lens.draft_chapter_status_materialized': 'Materialized',
  'idea_lens.draft_chapter_status_unmaterialized': 'Board only',
  'idea_lens.draft_chapter_linked_section': 'Linked section',
  'idea_lens.delete_draft_chapter': 'Delete chapter',
  'idea_lens.delete_draft_chapter_confirm': 'Deleting "{title}" will undo its grouping on the board. Continue?',
  'idea_lens.delete_draft_chapter_has_nodes': 'Move or delete ideas before deleting the chapter.',
  'idea_lens.board_group_status_draft_short': 'Draft',
  'idea_lens.board_group_status_in_manuscript_short': 'Live',
  'idea_lens.board_group_status_primary_here_short': 'Here',
  'idea_lens.board_group_status_secondary_here_short': 'Support',
  'idea_lens.materialize_cta': 'Add to structure',
  'idea_lens.materialize_modal_title': 'Convert to real chapter',
  'idea_lens.materialize_title_label': 'Chapter title',
  'idea_lens.materialize_title_placeholder': 'Chapter name…',
  'idea_lens.materialize_preview_label': 'Preview',
  'idea_lens.materialize_parent_label': 'Parent chapter (optional)',
  'idea_lens.materialize_parent_none': 'No parent',
  'idea_lens.materialize_confirm': 'Create',
  'idea_lens.materialize_cancel': 'Cancel',
  'idea_lens.coverage_title': 'Coverage',
  'idea_lens.coverage_mark': 'Mark as written',
  'idea_lens.coverage_unmark': 'Unmark',
  'idea_lens.not_linked_hint': 'This idea is not assigned to this section. Open it on the board to link it.',
  'idea_lens.primary_location_help_title': 'Main location',
  'idea_lens.primary_location_help_body': 'This menu changes where the idea lives right away. "Use active section" is just a shortcut, and "Clear main location" leaves it without a primary placement.',
  'idea_lens.primary_location_current': 'It now lives in {location}',
  'idea_lens.primary_location_empty': 'It does not have a main location yet.',
  'idea_lens.active_section_label': 'Active section',
  'idea_lens.active_section_description': 'This is the section currently open in the manuscript.',
  'idea_lens.active_section_empty': 'There is no active section.',
  'idea_lens.use_active_section_short': 'Use active section',
  'idea_lens.use_active_section_tooltip': 'Shortcut to assign the currently open section as the main location.',
  'idea_lens.clear_primary_location': 'Clear main location',
  'idea_lens.clear_primary_location_tooltip': 'Leaves this idea without a main location. This is the same as choosing the empty option in the menu.',
  'idea_lens.secondary_locations_help_title': 'Secondary appearances',
  'idea_lens.secondary_locations_help_body': 'Use them to mark where else an idea may appear without moving its main location.',
  'idea_lens.coverage_help_title': 'Coverage',
  'idea_lens.coverage_help_body': 'Mark whether you already wrote this idea in the active section. It does not change where the idea lives.',
  'idea_lens.connections_help_title': 'Connections',
  'idea_lens.connections_help_body': 'Shows which ideas go out from this one and which ones lead into it on the board.',
  'idea_lens.connections_outgoing_title': 'Outgoing from this idea',
  'idea_lens.connections_incoming_title': 'Incoming to this idea',
  'idea_lens.connections_outgoing_empty': 'There are no outgoing connections.',
  'idea_lens.connections_incoming_empty': 'There are no incoming connections.',
  'idea_lens.no_relations_bidirectional': 'There are no incoming or outgoing connections.',
  'idea_lens.filter_active_count': 'linked ideas',
  'idea_lens.hint_board_group': 'Groups this idea on the board. When the group becomes a real chapter, a green chip appears.',
  'idea_lens.hint_coverage': 'Did you write about this idea in the active section? Mark it as written.',
  'idea_lens.hint_manuscript_chapter': 'The exact manuscript section where this idea belongs.',
  'idea_lens.active_section_ideas': 'Ideas for',
  'idea_lens.draft_chapter_placeholder': 'Draft chapter name',
  'idea_lens.draft_chapter_select_placeholder': 'Choose draft chapter',
  'idea_lens.draft_chapter_assigned': 'It now lives in {title}.',
  'idea_lens.draft_chapter_empty': 'It does not belong to any draft chapter yet.',
  'idea_lens.create_draft_chapter': 'Create chapter',
  'idea_lens.assign_selected': 'Assign selection',
  'idea_lens.clear_group': 'Remove from group',
  'idea_lens.assign_select_target_hint': 'Choose a draft chapter first so the selection has a clear destination.',
  'idea_lens.delete_group': 'Delete group',
  'idea_lens.multi_assign_hint': 'Group these {count} ideas inside a local draft chapter.',
  'idea_lens.multi_select_connections_hint': 'Connections are edited one idea at a time. Keep multi-select for grouping.',
  'idea_lens.unplaced_label': 'No section yet',
  'idea_lens.relations': 'Relations',
  'idea_lens.relation_target_placeholder': 'Relate to another node',
  'idea_lens.add_relation': 'Connect',
  'idea_lens.remove_relation': 'Remove',
  'idea_lens.no_relations': 'There are no outgoing relations for this node yet.',
  'idea_lens.no_relations_short': 'No relations yet.',
  'idea_lens.board_connection_title': 'Connections',
  'idea_lens.board_connection_hint': 'Use them to mark continuity, support, or contrast.',
  'idea_lens.start_connection': 'Connect idea',
  'idea_lens.cancel_connection': 'Cancel connection',
  'idea_lens.connection_pick_target': 'Choose another idea on the board to connect it.',
  'idea_lens.connection_use_badge_hint': 'Select an idea and use the badge connector to link it.',
  'idea_lens.connect_target_placeholder': 'Connect to another idea',
  'idea_lens.connection_count': '{count}',
  'idea_lens.select_hint': 'Select an idea to place it and sharpen its angle.',
  'idea_lens.select_board_hint': 'Select an idea to expand it, move it, or connect it.',
  'idea_lens.select_manuscript_hint': 'Select an idea to place it in the manuscript.',
  'idea_lens.open_surface': 'Open idea board',
  'idea_lens.open_surface_tooltip': 'Open the essay idea board',
  'idea_lens.open_workspace': 'Open map',
  'idea_lens.back_to_editor': 'Back to manuscript',
  'idea_lens.back_to_global_lens': 'Back to Atlas',
  'idea_lens.side_toggle_label': 'Linked ideas',
  'idea_lens.side_toggle_empty_tooltip': 'Bring to the board',
  'idea_lens.toast_covered': 'Idea captured in the text!',
  'idea_lens.no_notes': 'No notes yet.',
  'idea_lens.with_ideas_tooltip': '{count} linked idea|{count} linked ideas',
  'idea_lens.side_toggle_tooltip': 'See ideas linked to this section',
  'idea_lens.side_badge': 'Idea Lens',
  'idea_lens.side_title': 'Ideas for this part',
  'idea_lens.side_empty': 'There are no ideas here yet.',
  'idea_lens.side_empty_cta': 'Create ideas',
  'idea_lens.side_direct': 'Linked to this section',
  'idea_lens.side_contextual': 'Chapter ideas',
  'idea_lens.side_contextual_hint': 'Showing chapter ideas first so you can keep the thread in view.',
  'idea_lens.covered': 'Covered',
  'idea_lens.open': 'Open',
  'idea_lens.mark_covered': 'Mark covered',
  'idea_lens.mark_open': 'Reopen',
  'idea_lens.arrange_selected': 'Arrange',
  'idea_lens.arrange_selected_hint': 'Arrange selected ideas into a grid',
  'idea_lens.new_placeholder_board_hint': 'Press N to focus · Enter to create',
  'idea_lens.link_count': '{count} links',
  'idea_lens.covered_count': '{count} covered',
  'idea_lens.side_close': 'Close ideas panel',
  'idea_lens.insert_idea': 'Insert in manuscript',
  'idea_lens.develop_here': 'Develop here',
  'idea_lens.mention_here': 'Mention here',
  'idea_lens.status_inserted': 'Inserted',
  'idea_lens.status_pending': 'Pending',
  'idea_lens.toast_inserted': 'Idea inserted in the manuscript!',
  'idea_lens.level_label': 'Level {n}',
  'idea_lens.show_next_level': 'Show next level',
  'idea_lens.all_levels_shown': 'All levels revealed',
  'idea_lens.collapse_chapter': 'Collapse chapter',
  'idea_lens.expand_chapter': 'Expand chapter',
  'idea_lens.connect_chapter': 'Connect to another chapter',
  'idea_lens.zoom_in': 'Zoom in',
  'idea_lens.zoom_out': 'Zoom out',
  'idea_lens.zoom_fit': 'Fit',
  'idea_lens.sequence_label_placeholder': 'e.g. Prologue, I, A',
  'idea_lens.sequence_label_tooltip': 'Short chapter label. Click to edit it and use something like Prologue, I, or A.',
  'idea_lens.numbering_style_arabic': 'Arabic numbering',
  'idea_lens.numbering_style_roman': 'Roman numbering',
  'idea_lens.numbering_style_alpha': 'Alphabetic numbering',
  'idea_lens.header_block_navigation_eyebrow': 'Navigation',
  'idea_lens.header_block_navigation_title': 'Move between board and manuscript',
  'idea_lens.header_block_navigation_help_title': 'Switch surfaces',
  'idea_lens.header_block_navigation_help_body': 'Use this block to jump between the board and the manuscript without losing your working context.',
  'idea_lens.header_block_create_eyebrow': 'Create ideas',
  'idea_lens.header_block_create_title': 'Capture the idea and choose numbering',
  'idea_lens.header_block_create_help_title': 'Create and number',
  'idea_lens.header_block_create_help_body': 'A new idea appears near the active one. Numbering affects draft chapters and their materialization only.',
  'idea_lens.header_numbering_hint': 'Numbering affects draft chapters and their step into the manuscript.',
  'idea_lens.header_block_group_eyebrow': 'Group and materialize',
  'idea_lens.header_block_group_title': 'Turn the selection into a draft chapter',
  'idea_lens.header_block_group_help_title': 'Grouping is not materializing',
  'idea_lens.header_block_group_help_body': 'This block groups ideas on the board. Creating the real manuscript section is still a separate step.',
  'idea_lens.create_chapter_with_selection': 'Create chapter with selection',
  'idea_lens.reveal_next': '{n} more →',
  'idea_lens.draft_chapter_suggested_title': 'Draft chapter',
  'idea_lens.primary_location_title': 'Main location',
  'idea_lens.secondary_locations_title': 'Secondary appearances',
  'idea_lens.coverage_mark_here': 'Mark as written here',
  'idea_lens.coverage_unmark_here': 'Reopen here',
  'idea_lens.primary_here_badge': 'Lives here',
  'idea_lens.secondary_here_badge': 'Also here',
  'idea_lens.primary_location_inline': 'Lives in {location}',
  'idea_lens.secondary_count_inline': '+{count} appearances',
  'idea_lens.secondary_none_inline': 'No secondary appearances',
  'idea_lens.overlay_primary_here': 'Ideas whose main location is here',
  'idea_lens.overlay_secondary_here': 'Ideas that also appear here',
  'idea_lens.overlay_contextual': 'Chapter ideas',
  'idea_lens.onboarding_badge': 'Start here',
  'idea_lens.onboarding_step_1': '1. Create an idea',
  'idea_lens.onboarding_step_2': '2. Link it to a section',
  'idea_lens.onboarding_step_3': '3. Mark it covered',
  'idea_lens.onboarding_step_board_1': '1. Create an idea',
  'idea_lens.onboarding_step_board_2': '2. Move it or connect it',
  'idea_lens.onboarding_step_board_3': '3. Bring it into the manuscript',
  'idea_lens.dismiss_onboarding': 'Hide help',
  'idea_lens.missing_target': 'Missing node',
  'idea_lens.node_type.idea': 'Idea',
  'idea_lens.node_type.question': 'Question',
  'idea_lens.node_type.example': 'Example',
  'idea_lens.node_type.bridge': 'Bridge',
  'idea_lens.edge_type.supports': 'Supports',
  'idea_lens.edge_type.leads_to': 'Leads to',
  'idea_lens.edge_type.contrasts': 'Contrasts',
  'lens.run': 'Run analysis',
  'lens.run_busy': 'Analyzing…',
  'lens.run_error': 'Lens analysis could not be completed.',
  'lens.anchor_not_found': 'The current fragment for this observation could not be located.',
  'lens.no_run': 'There is no analysis yet for this lens and scope.',
  'lens.no_run_hint': 'Choose another combination or run the analysis.',
  'lens.active_run': 'Active run',
  'lens.no_findings': 'No priority findings were detected in this run.',
  'lens.all_resolved': 'All findings in this run are marked as resolved.',
  'lens.recent_runs': 'Recent runs',
  'lens.no_recent_runs': 'There is no local Lens history yet.',
  'lens.show_more': 'Show more',
  'lens.show_less': 'Show less',
  'lens.results_count': '{count} findings',
  'lens.open_count': '{count} open',
  'lens.resolved_count': '{count} resolved',
  'lens.resolve': 'Resolve',
  'lens.reopen': 'Reopen',
  'lens.resolved_group': 'Resolved',
  'lens.resolved_badge': 'Resolved',
  'lens.before_excerpt': 'Before',
  'lens.after_excerpt': 'Now',
  'lens.jump_to_fragment': 'Jump to fragment',
  'lens.scope_target': 'Target',
  'lens.outdated_badge': 'Outdated',
  'lens.outdated_hint': 'The text or export configuration changed after this analysis. Rerun it.',
  'lens.rerun': 'Rerun',
  'lens.publication_context': 'KDP preflight',
  'lens.publication_action.focus_setting': 'Open setting',
  'lens.publication_action.focus_preflight': 'View preflight',
  'lens.publication_action.preview_pages': 'View affected pages',
  'lens.publication_action.manual_review': 'Manual review',
  'lens.publication_action.open_section': 'Open section',
  'lens.publication_pages': '{count} page(s): {pages}',
  'lens.section_fallback': 'Section',
  'lens.scope.section': 'Section',
  'lens.scope.chapter': 'Chapter',
  'lens.scope.project': 'Project',
  'lens.name.clarity': 'Clarity',
  'lens.name.structure': 'Structure',
  'lens.name.logic': 'Logic',
  'lens.name.narrative': 'Narrative',
  'lens.name.audience': 'Audience',
  'lens.name.translation': 'Translation',
  'lens.name.publication': 'Publication',
  'lens.severity.low': 'Low',
  'lens.severity.medium': 'Medium',
  'lens.severity.high': 'High',

  'settings.close_project': 'Close project',
  'settings.edit_project': 'Edit project',
  'settings.ai_assistant': 'AI Assistant',
  'settings.legacy_ai': 'AI Assistant (legacy)',
  'settings.legacy_ai_description': 'Transitional chat surface. Not the primary writing workflow.',
  'settings.prepare_export': 'Prepare & Export',
  'settings.prepare_export_description': 'Substack-ready Markdown/HTML and manual key handoff',

  'prepare_export.title': 'Prepare & Export',
  'prepare_export.subtitle': 'Prepare a local publishing package without depending on cloud sync yet.',
  'prepare_export.tab_prepare': 'Prepare',
  'prepare_export.tab_export': 'Export',
  'prepare_export.tab_key': 'Key Handoff',
  'prepare_export.no_project': 'Open a project to prepare and export.',
  'prepare_export.prepare_heading': 'Local preparation',
  'prepare_export.prepare_help': 'Choose the version, adjust title/subtitle, and define the manual byline before exporting.',
  'prepare_export.version_label': 'Version',
  'prepare_export.version_final': 'final',
  'prepare_export.no_versions': 'No versions available',
  'prepare_export.title_label': 'Title',
  'prepare_export.subtitle_label': 'Subtitle',
  'prepare_export.contributors_label': 'Contributors / byline',
  'prepare_export.add_contributor': 'Add contributor',
  'prepare_export.remove_contributor': 'Remove',
  'prepare_export.contributor_placeholder': 'Contributor {index}',
  'prepare_export.include_byline': 'Include byline in output',
  'prepare_export.preview_heading': 'Quick preview',
  'prepare_export.preview_version': 'Version: {version}',
  'prepare_export.preview_sections': 'Sections: {count}',
  'prepare_export.preview_words': 'Words: {count}',
  'prepare_export.untitled': 'Untitled',
  'prepare_export.go_to_export': 'Go to Export',
  'prepare_export.export_heading': 'Substack-ready output',
  'prepare_export.export_help': 'Copy or download deterministic Markdown/HTML generated from the project tree.',
  'prepare_export.copy_markdown': 'Copy Markdown',
  'prepare_export.copy_html': 'Copy HTML',
  'prepare_export.download_markdown': 'Download .md',
  'prepare_export.download_html': 'Download .html',
  'prepare_export.format_kdp_pdf': 'KDP PDF',
  'prepare_export.premium_lock_publication': 'Publication export is available on Premium.',
  'prepare_export.premium_lock_publication_hint': 'Preview, preflight, and KDP export stay visible in Premium without exposing billing yet.',
  'prepare_export.rich_formatting': 'Rich formatting (bold, italic, quotes, and lists)',
  'prepare_export.print_pagination': 'Print pagination',
  'prepare_export.syntax_help': 'Supported syntax: `**bold**`, `*italic*`, `> quote`, `- list`, `1. numbered list`.',
  'prepare_export.preview_markdown': 'Markdown preview',
  'prepare_export.preview_html': 'HTML preview',
  'prepare_export.preview_scale_fit_width': 'Fit width',
  'prepare_export.preview_scale_fit_page': 'Fit page',
  'prepare_export.preview_scale_100': '100%',
  'prepare_export.html_source': 'HTML source',
  'prepare_export.kdp_heading': 'KDP Interior PDF v1',
  'prepare_export.kdp_help': 'Typeset a 6x9 cream/no-bleed paperback interior with explicit pages. Chromium is the recommended target; Safari is allowed as a best-effort export path.',
  'prepare_export.kdp_help_cta': 'Help',
  'prepare_export.kdp_help_inline': 'Start with the preset, typography, special pages, and preflight. Insert figure and citation already write the token for you; markdown-lite here also supports asset:// figures, figure references, citations, equations, alpha lists, and epigraph blocks. For the book body, do not add decorative blank lines: let KDP control vertical rhythm.',
  'prepare_export.kdp_preview_panel': 'Preview KDP',
  'prepare_export.kdp_preview_panel_help': 'Open the preview first; keep the rest collapsed unless you need to adjust something. Preview scale stays local to this device.',
  'prepare_export.kdp_section_profile': 'Profile & composition',
  'prepare_export.kdp_section_profile_help': 'Preset, composition rules, page numbering, warnings, and bottom-fit behavior. This block is part of the project KDP configuration.',
  'prepare_export.kdp_section_typography': 'Typography & density',
  'prepare_export.kdp_section_metadata': 'Title page & metadata',
  'prepare_export.kdp_help_modal_title': 'Quick guide for KDP and markdown-lite',
  'prepare_export.kdp_help_modal_subtitle': 'What matters first and how to insert special blocks without leaving the book workflow.',
  'prepare_export.kdp_help_block_important_title': 'What matters first',
  'prepare_export.kdp_help_block_important_body': '1. Pick a preset and font pairing.\n2. Review title page and special pages.\n3. Check preflight and inner gutter.\n4. Export in Chromium for the most reliable final PDF.',
  'prepare_export.kdp_help_block_markdown_title': 'Basic markdown',
  'prepare_export.kdp_help_block_markdown_body': '**bold**\n*italic*\n> quote\n- list\n1. numbered list',
  'prepare_export.kdp_help_block_advanced_title': 'KDP blocks',
  'prepare_export.kdp_help_block_advanced_body': 'Figure: ![Caption](asset://<assetId>)\nFigure reference: [@fig:risks-dashboard]\nCitation: [@smith2024]\nCitation with locator: [@smith2024, p. 42]\n\nEquations:\n$$\n\\\\frac{a}{b}\n$$\n$$\n\\\\sqrt{x}\n$$\nInline: x^2 and x_i\n\na. alphabetic list\nb. second item\n\n:::epigraph\nEverything is still unwritten.\n-- Author\n:::\n\n:::recuadro\ntitle: Context\nvariant: note\n\nCallout body text.\n:::',
  'prepare_export.kdp_help_block_parts_title': 'Parts and chapters',
  'prepare_export.kdp_help_block_parts_body': 'If a root section has children and its title starts with "Parte" or "Part", KDP treats it as a part opener. That root gets its own opening page and its direct children are laid out as the chapters inside that part.',
  'prepare_export.kdp_help_block_sync_title': 'What gets saved where',
  'prepare_export.kdp_help_block_sync_body': 'Project structure, text, and the canonical KDP configuration now travel with the project itself. On this device, the modal also remembers the active tab, the open KDP panel, and the local export state when you reopen it. Preview scale and the onboarding seen-state still stay local here.',
  'prepare_export.kdp_help_modal_done': 'Close guide',
  'prepare_export.kdp_profile_label': 'Preset',
  'prepare_export.kdp_profile_value': 'Paperback 6x9 · black ink · cream paper · no bleed',
  'prepare_export.kdp_profile_value_label': 'Active preset',
  'prepare_export.kdp_preset_save': 'Save preset',
  'prepare_export.kdp_preset_overwrite': 'Overwrite',
  'prepare_export.kdp_preset_delete': 'Delete preset',
  'prepare_export.kdp_preset_save_prompt': 'Name for the new KDP preset',
  'prepare_export.kdp_preset_overwrite_prompt': 'Name to overwrite the current KDP preset',
  'prepare_export.kdp_preset_saved': 'KDP preset saved.',
  'prepare_export.kdp_preset_overwritten': 'KDP preset updated.',
  'prepare_export.kdp_preset_deleted': 'KDP preset deleted.',
  'prepare_export.kdp_front_matter': 'Include title page + copyright page',
  'prepare_export.kdp_include_toc': 'Include table of contents',
  'prepare_export.kdp_toc_depth': 'TOC depth',
  'prepare_export.kdp_toc_depth_0': 'Chapters only',
  'prepare_export.kdp_toc_depth_1': 'Chapters + subsections',
  'prepare_export.kdp_toc_depth_2': 'Up to two levels',
  'prepare_export.kdp_right_page': 'Start chapters on right-hand pages',
  'prepare_export.kdp_page_numbers': 'Page numbers',
  'prepare_export.kdp_page_numbers_position': 'Page number position',
  'prepare_export.kdp_page_numbers_center': 'Centered footer',
  'prepare_export.kdp_page_numbers_right': 'Outer footer',
  'prepare_export.kdp_running_heads': 'Book-style running heads',
  'prepare_export.kdp_scene_break': 'Scene break',
  'prepare_export.kdp_scene_break_asterism': 'Asterism',
  'prepare_export.kdp_scene_break_blank': 'Blank space',
  'prepare_export.kdp_typography_heading': 'Typography & Layout',
  'prepare_export.kdp_typography_help': 'Closed preset for a calmer text box, better vertical rhythm, and cleaner print pages without exposing freeform layout controls.',
  'prepare_export.kdp_font_pair': 'Font pairing',
  'prepare_export.kdp_title_alignment': 'Title page alignment',
  'prepare_export.kdp_title_alignment_centered': 'Centered',
  'prepare_export.kdp_title_alignment_left': 'Left aligned',
  'prepare_export.kdp_title_page_heading': 'Title Page Preview Settings',
  'prepare_export.kdp_title_page_help': 'Adjust the title, subtitle, and byline shown on the interior title page of the KDP PDF.',
  'prepare_export.kdp_layout_density': 'Density',
  'prepare_export.kdp_layout_compact': 'Compact',
  'prepare_export.kdp_layout_standard': 'Standard',
  'prepare_export.kdp_layout_airy': 'Airy',
  'prepare_export.kdp_bottom_fit_mode': 'Bottom fit mode',
  'prepare_export.kdp_bottom_fit_balanced': 'Balanced',
  'prepare_export.kdp_bottom_fit_balanced_plus': 'Balanced plus',
  'prepare_export.kdp_subheading_keep_with_next': 'Subheading with lead-in',
  'prepare_export.kdp_subheading_keep_with_next_off': 'Off',
  'prepare_export.kdp_subheading_keep_with_next_keep_with_2_lines': 'Keep with 2 lines',
  'prepare_export.kdp_allow_warnings': 'Allow export with warnings',
  'prepare_export.kdp_preflight': 'KDP preflight',
  'prepare_export.kdp_page_count': 'Real pages: {count}',
  'prepare_export.kdp_gutter': 'Inner gutter: {value}"',
  'prepare_export.kdp_export_pdf': 'Print / Save PDF',
  'prepare_export.kdp_download_report': 'Download preflight .json',
  'prepare_export.kdp_download_html': 'Download KDP HTML',
  'prepare_export.kdp_print_hint': 'The officially supported final PDF is generated in Chromium/Chrome. Use "Save as PDF", actual size, and disable browser headers/footers.',
  'prepare_export.kdp_print_hint_safari': 'Safari is allowed as a best-effort export path. If the final PDF looks wrong, download the KDP HTML and open it in Chromium/Chrome to use "Save as PDF" at actual size with browser headers/footers disabled.',
  'prepare_export.kdp_issue_error': 'Error',
  'prepare_export.kdp_issue_warning': 'Warning',
  'prepare_export.kdp_issue_pass': 'OK',
  'prepare_export.kdp_manual_checks': 'Manual checks before uploading to KDP',
  'prepare_export.kdp_engine_checks': 'Automatic layout engine validations',
  'prepare_export.kdp_preview': 'KDP preview',
  'prepare_export.kdp_report_downloaded': 'KDP preflight downloaded.',
  'prepare_export.kdp_html_downloaded': 'KDP HTML downloaded.',
  'prepare_export.kdp_print_opened': 'KDP print window opened.',
  'prepare_export.kdp_print_blocked': 'The browser blocked the print window. Allow pop-ups and try again.',
  'prepare_export.kdp_error_preview': 'Could not generate the KDP preview.',
  'prepare_export.kdp_special_pages': 'Special Pages',
  'prepare_export.kdp_special_pages_help': 'Manage persistent editorial pages for the KDP interior: front matter, inserts between root chapters, and back matter.',
  'prepare_export.kdp_parts_callout_title': 'How Parts work in beta',
  'prepare_export.kdp_parts_callout_body': 'There is no separate persisted Part entity yet. If a root section has children and its title starts with "Parte" or "Part", KDP will lay it out as a standalone part opener and treat its direct children as chapters.',
  'prepare_export.kdp_add_special_page': 'Add page',
  'prepare_export.kdp_template_acknowledgements': 'Acknowledgements template',
  'prepare_export.kdp_template_copyright': 'Copyright + ISBN template',
  'prepare_export.kdp_template_blank': 'Blank page',
  'prepare_export.kdp_template_about_author': 'About the author template',
  'prepare_export.kdp_template_bibliography': 'Bibliography template',
  'prepare_export.kdp_template_notes': 'Notes template',
  'prepare_export.kdp_page_disabled': 'Disabled',
  'prepare_export.kdp_move_up': 'Move up',
  'prepare_export.kdp_move_down': 'Move down',
  'prepare_export.kdp_delete_page': 'Delete',
  'prepare_export.kdp_page_kind': 'Page type',
  'prepare_export.kdp_page_placement': 'Placement',
  'prepare_export.kdp_page_anchor': 'Anchor root chapter',
  'prepare_export.kdp_page_anchor_empty': 'No chapter available',
  'prepare_export.kdp_page_title': 'Visible title',
  'prepare_export.kdp_page_body': 'Content (markdown-lite)',
  'prepare_export.kdp_page_enabled': 'Include in interior',
  'prepare_export.kdp_page_right_start': 'Force right-hand page start',
  'prepare_export.kdp_focus_pages': 'Affected pages in preview: {pages}',
  'prepare_export.kdp_focus_assets': 'Assets to review: {assets}',
  'prepare_export.kdp_open_section': 'Open section',
  'prepare_export.loading_preview': 'Generating preview…',
  'prepare_export.copied_markdown': 'Markdown copied.',
  'prepare_export.copied_html': 'HTML copied.',
  'prepare_export.copy_failed': 'Could not copy to clipboard.',
  'prepare_export.downloaded_markdown': 'Markdown file downloaded.',
  'prepare_export.downloaded_html': 'HTML file downloaded.',
  'prepare_export.key_heading': 'Manual key handoff',
  'prepare_export.key_help': 'While multi-device sync remains unresolved, the project key is shared manually.',
  'prepare_export.key_fingerprint': 'Fingerprint',
  'prepare_export.key_missing': 'No local key',
  'prepare_export.key_placeholder': 'Generate or import a key in Gray Matter Zone before sharing it manually.',
  'prepare_export.show_key': 'Show',
  'prepare_export.hide_key': 'Hide',
  'prepare_export.copy_key': 'Copy key',
  'prepare_export.download_key': 'Download key',
  'prepare_export.import_key': 'Import/Replace',
  'prepare_export.key_handoff_heading': 'Recommended flow',
  'prepare_export.key_handoff_body': 'This phase avoids incomplete cloud collaboration. Share output and key outside the app until SWK/sync is resolved.',
  'prepare_export.key_step_one': 'Export Markdown or HTML from this screen for review or publishing.',
  'prepare_export.key_step_two': 'Share the project key through a secure channel only if another person needs to decrypt local content.',
  'prepare_export.key_step_three': 'Once the other device has the key, it can import/replace it manually and work locally.',
  'prepare_export.error_versions': 'Could not load project versions.',
  'prepare_export.error_preview': 'Could not generate the export preview.',
}
